import styles from "../../styles/PLMApp.module.css";
// src/components/plm/StepDrawer.jsx

import { useState, useEffect, useRef, useCallback } from "react";

import { useDocuments, useActivityLog } from "../../hooks/hooks";
import { saveStep, logActivity } from "../../firebase/firebaseHelpers";
import {
  getStatusLabel,
  getFileIcon,
  formatDateInput,
  timeAgo,
} from "../../utils/utils";
import StatusToggle from "./StatusToggle";
import UploadPanel from "./UploadPanel";

export default function StepDrawer({
  step,
  docId,
  category,
  onClose,
  onStatusChange,
  supplierId,
  orderId,
  isAdmin,
}) {
  const [localStep, setLocalStep] = useState({ ...step });
  const [saveState, setSaveState] = useState("idle");
  const saveTimer = useRef();

  // No order selected at all — blocks everything including upload
  const isReadOnly = !supplierId || !orderId;

  // Order selected but not admin — blocks workflow fields, upload still open
  const isFieldLocked = isReadOnly || !isAdmin;

  const docs = useDocuments(supplierId, orderId, docId);
  const log = useActivityLog(supplierId, orderId, docId);

  useEffect(() => {
    setLocalStep({ ...step });
  }, [step]);

  useEffect(() => {
    return () => clearTimeout(saveTimer.current);
  }, []);

  const handleChange = useCallback(
    (field, value) => {
      setLocalStep((p) => ({ ...p, [field]: value }));

      if (isFieldLocked) return;

      setSaveState("saving");
      clearTimeout(saveTimer.current);

      saveTimer.current = setTimeout(async () => {
        try {
          await saveStep(supplierId, orderId, docId, { [field]: value });

          if (field === "notes") {
            await logActivity(supplierId, orderId, docId, "Notes updated");
          }

          setSaveState("saved");
          setTimeout(() => setSaveState("idle"), 2000);
        } catch (e) {
          console.error(e);
          setSaveState("idle");
        }
      }, 800);
    },
    [docId, supplierId, orderId, isFieldLocked],
  );

  const handleStatusChange = async (newStatus) => {
    setLocalStep((p) => ({ ...p, status: newStatus }));

    if (isFieldLocked) return;

    await saveStep(supplierId, orderId, docId, { status: newStatus });

    await logActivity(
      supplierId,
      orderId,
      docId,
      "Status changed",
      `→ ${getStatusLabel(newStatus)}`,
    );

    onStatusChange?.(newStatus);
  };

  // Save indicator message
  const saveIndicatorText = () => {
    if (isReadOnly) return "Select an order to edit";
    if (!isAdmin) return "View only — no edit access";
    if (saveState === "saving") return "Saving…";
    if (saveState === "saved") return "✓ Saved";
    return "Auto-save enabled";
  };

  return (
    <>
      <div className={styles.drawerOverlay} onClick={onClose} />

      <aside className={styles.drawer}>
        <div className={styles.drawerHeader}>
          <div className={styles.drawerHeaderTop}>
            <div>
              <div className={styles.drawerCategoryTag}>{category.name}</div>
              <div className={styles.drawerTitle}>
                {localStep.stepTitle || step.title}
              </div>
            </div>

            <button className={styles.drawerCloseBtn} onClick={onClose}>
              ✕
            </button>
          </div>

          <div className={styles.drawerStatusRow}>
            <StatusToggle
              currentStatus={localStep.status || "not_started"}
              disabled={isFieldLocked}
              onSelect={handleStatusChange}
            />

            <span
              style={{
                fontSize: 9,
                color: "var(--text-muted)",
                letterSpacing: 1,
              }}
            >
              Step {localStep.stepNumber}
            </span>
          </div>
        </div>

        <div className={styles.drawerBody}>
          {/* ── Planned Dates ────────────────────────────────────────────── */}
          <div className={styles.drawerSection}>
            <div className={styles.drawerSectionTitle}>Planned Dates</div>

            <div className={styles.dateGrid}>
              {[
                ["startDate", "Start Date"],
                ["endDate", "End Date"],
              ].map(([field, label]) => (
                <div key={field} className={styles.dateField}>
                  <label className={styles.fieldLabel}>{label}</label>
                  <input
                    type="date"
                    className={styles.fieldInput}
                    value={formatDateInput(localStep[field])}
                    disabled={isFieldLocked}
                    onChange={(e) =>
                      handleChange(
                        field,
                        e.target.value ? new Date(e.target.value) : null,
                      )
                    }
                  />
                </div>
              ))}
            </div>
          </div>

          {/* ── Assignment ───────────────────────────────────────────────── */}
          <div className={styles.drawerSection}>
            <div className={styles.drawerSectionTitle}>Assignment</div>

            <div className={styles.dateGrid}>
              <div className={styles.dateField}>
                <label className={styles.fieldLabel}>Active Handler</label>
                <input
                  type="text"
                  className={styles.fieldInput}
                  placeholder="Active Handler"
                  value={localStep.handler || ""}
                  disabled={isFieldLocked}
                  onChange={(e) => handleChange("handler", e.target.value)}
                />
              </div>

              <div className={styles.dateField}>
                <label className={styles.fieldLabel}>Priority</label>
                <select
                  className={styles.fieldSelect}
                  value={localStep.priority || "medium"}
                  disabled={isFieldLocked}
                  onChange={(e) => handleChange("priority", e.target.value)}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
          </div>

          {/* ── Notes ───────────────────────────────────────────────────── */}
          <div className={styles.drawerSection}>
            <div className={styles.drawerSectionTitle}>Notes</div>

            <textarea
              className={styles.notesArea}
              placeholder="Add notes, decisions, observations…"
              value={localStep.notes || ""}
              disabled={isFieldLocked}
              onChange={(e) => handleChange("notes", e.target.value)}
            />

            <div
              className={`${styles.saveIndicator} ${
                saveState === "saving"
                  ? styles.saveIndicatorSaving
                  : saveState === "saved"
                    ? styles.saveIndicatorSaved
                    : ""
              }`}
            >
              {saveIndicatorText()}
            </div>
          </div>

          {/* ── Documents — upload open to all, gated only on no order ─── */}
          <div className={styles.drawerSection}>
            <div className={styles.drawerSectionTitle}>Documents</div>

            {!isReadOnly && (
              <UploadPanel
                stepId={docId}
                categoryId={category.id}
                supplierId={supplierId}
                orderId={orderId}
                isAdmin={isAdmin}
                currentStatus={localStep.status || "not_started"}
                onStatusChange={(newStatus) =>
                  setLocalStep((p) => ({ ...p, status: newStatus }))
                }
              />
            )}

            {docs.length > 0 && (
              <div className={styles.docList}>
                {docs.map((d) => (
                  <div key={d.id} className={styles.docItem}>
                    <span className={styles.docIcon}>
                      {getFileIcon(d.name)}
                    </span>
                    <span className={styles.docName}>{d.name}</span>
                    <span className={styles.docMeta}>{d.tag}</span>
                    <a
                      href={d.url}
                      target="_blank"
                      rel="noreferrer"
                      className={styles.docLink}
                    >
                      ↗
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Activity Log ─────────────────────────────────────────────── */}
          <div className={styles.drawerSection}>
            <div className={styles.drawerSectionTitle}>Activity Log</div>

            {log.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>📋</div>
                <div className={styles.emptyStateText}>No activity yet</div>
              </div>
            ) : (
              <div className={styles.activityLog}>
                {log.map((entry, i) => (
                  <div key={entry.id} className={styles.activityEntry}>
                    {i < log.length - 1 && (
                      <div className={styles.activityLine} />
                    )}

                    <div className={styles.activityDot}>⚬</div>

                    <div className={styles.activityText}>
                      <div className={styles.activityAction}>
                        {entry.action}
                        {entry.detail && (
                          <span
                            style={{
                              color: "var(--text-muted)",
                              marginLeft: 6,
                            }}
                          >
                            {entry.detail}
                          </span>
                        )}
                      </div>

                      <div className={styles.activityTime}>
                        {timeAgo(entry.createdAt)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={styles.drawerFooter}>
          <button
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </aside>
    </>
  );
}
